import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Ban, Printer, Download, Receipt as ReceiptIcon } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { getQueue } from "../lib/offline";
import { rp, fmtDate, METHODS, rangeFor, downloadCSV, todayWIB } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useHardware } from "../context/HardwareContext";
import { PageWrap, Empty } from "../components/common";
import { DateFilter } from "../components/DateFilter";
import { ReceiptPaper } from "../components/Receipt";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const today = todayWIB();

export default function Transactions() {
  const { can, store } = useAuth();
  const { printReceipt, width } = useHardware();
  const [preset, setPreset] = useState("today");
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [q, setQ] = useState("");
  const [method, setMethod] = useState("all");
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [voidT, setVoidT] = useState(null);

  const [queue, setQueue] = useState(getQueue());
  useEffect(() => {
    const h = () => { setQueue(getQueue()); load(); };
    window.addEventListener("pos-queue", h);
    return () => window.removeEventListener("pos-queue", h);
  });
  const pendingRows = queue.map((q) => ({ ...q.tx, sync_error: q.error }));
  const allRows = [...pendingRows, ...rows];

  const load = useCallback(() => {
    const r = rangeFor(preset, from, to);
    api.get("/transactions", { params: { ...r, q, payment_method: method === "all" ? "" : method, limit: 2000 } }).then((x) => setRows(x.data)).catch(() => {});
  }, [preset, from, to, q, method]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const doVoid = async () => {
    try {
      const { data } = await api.post(`/transactions/${voidT.id}/void`);
      toast.success("Transaksi dibatalkan, stok dikembalikan");
      setSel(data);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setVoidT(null);
  };

  const ok = rows.filter((r) => r.status === "selesai");
  const exportCsv = () =>
    downloadCSV("transaksi.csv", [["Invoice", "Tanggal", "Kasir", "Pelanggan", "Metode", "Subtotal", "Diskon", "Pajak", "Total", "Status"],
      ...rows.map((t) => [t.invoice, fmtDate(t.created_at), t.cashier_name, t.customer_name, METHODS[t.payment_method], t.subtotal, t.discount, t.tax, t.total, t.status])]);

  return (
    <PageWrap>
      <div className="flex flex-col xl:flex-row gap-3 xl:items-center justify-between">
        <DateFilter {...{ preset, setPreset, from, setFrom, to, setTo }} />
        <div className="flex gap-2">
          <div className="relative flex-1 xl:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Cari invoice / pelanggan" value={q} onChange={(e) => setQ(e.target.value)} data-testid="tx-search-input" />
          </div>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="w-36" data-testid="tx-method-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Metode</SelectItem>
              {Object.entries(METHODS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="icon" onClick={exportCsv} title="Export CSV" data-testid="tx-export-btn"><Download className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="rounded-xl border bg-card p-4"><div className="text-xs text-muted-foreground">Jumlah Transaksi</div><div className="font-mono font-bold text-xl" data-testid="tx-count">{ok.length}</div></div>
        <div className="rounded-xl border bg-card p-4"><div className="text-xs text-muted-foreground">Total Penjualan</div><div className="font-mono font-bold text-xl text-primary" data-testid="tx-sum">{rp(ok.reduce((a, b) => a + b.total, 0))}</div></div>
        <div className="rounded-xl border bg-card p-4 hidden md:block"><div className="text-xs text-muted-foreground">Dibatalkan</div><div className="font-mono font-bold text-xl text-destructive">{rows.length - ok.length}</div></div>
      </div>
      <div className="rounded-2xl border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Invoice</TableHead>
              <TableHead className="hidden md:table-cell">Waktu</TableHead>
              <TableHead className="hidden lg:table-cell">Kasir</TableHead>
              <TableHead>Metode</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-center">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {allRows.map((t) => (
              <TableRow key={t.id} className="cursor-pointer" onClick={() => setSel(t)} data-testid={`tx-row-${t.id}`}>
                <TableCell className="font-mono text-sm font-semibold">{t.invoice}<div className="md:hidden text-xs text-muted-foreground font-sans font-normal">{fmtDate(t.created_at)}</div></TableCell>
                <TableCell className="hidden md:table-cell text-sm">{fmtDate(t.created_at)}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm">{t.cashier_name}</TableCell>
                <TableCell className="text-sm">{METHODS[t.payment_method]}</TableCell>
                <TableCell className="text-right font-mono font-bold">{rp(t.total)}</TableCell>
                <TableCell className="text-center">{t.pending ? <Badge variant="outline" className={t.sync_error ? "text-destructive" : "text-warning"} title={t.sync_error || ""} data-testid="tx-pending-badge">{t.sync_error ? "Gagal sinkron" : "Menunggu sinkron"}</Badge> : <Badge variant={t.status === "batal" ? "destructive" : "outline"}>{t.status === "batal" ? "Batal" : t.offline ? "Selesai (offline)" : "Selesai"}</Badge>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {allRows.length === 0 && <Empty icon={ReceiptIcon} text="Tidak ada transaksi pada periode ini." />}
      </div>
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-md max-h-[95vh] overflow-y-auto" data-testid="tx-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Detail Transaksi</DialogTitle>
            <DialogDescription className="font-mono">{sel?.invoice}</DialogDescription>
          </DialogHeader>
          {sel && store && (
            <>
              <div className="bg-muted/60 rounded-xl py-5 overflow-x-auto"><ReceiptPaper tx={sel} store={store} width={width} /></div>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => printReceipt(sel)} data-testid="tx-reprint-btn"><Printer className="h-4 w-4 mr-2" />Cetak Ulang</Button>
                {can("tx_void") && (
                  <Button variant="destructive" disabled={sel.status === "batal" || sel.pending} onClick={() => setVoidT(sel)} data-testid="tx-void-btn"><Ban className="h-4 w-4 mr-2" />Batalkan</Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog open={!!voidT} onOpenChange={(o) => !o && setVoidT(null)} title="Batalkan transaksi?" confirmText="Batalkan"
        description="Transaksi akan ditandai batal dan stok produk dikembalikan." onConfirm={doVoid} />
    </PageWrap>
  );
}
